import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const LandingPage: React.FC = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    setIsVisible(true);

    const handleScroll = () => {
      setScrollY(window.scrollY);

      // Update active section based on scroll position
      const sections = ['home', 'about', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const offsetTop = element.offsetTop;
          const offsetHeight = element.offsetHeight;

          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setActiveSection(sectionId);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className={`fixed top-0 w-full z-50 transition-all duration-300 ${scrollY > 50 ? 'bg-white/95 backdrop-blur-md shadow-lg border-b border-gray-200/50' : 'bg-white/90 backdrop-blur-md shadow-sm'
        }`}>
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center space-x-3 group">
              <div className="relative">
                <img src="/logo.png" alt="Maso Athu" className="w-10 h-10 transition-transform duration-200 group-hover:scale-110" />
                <div className="absolute inset-0 transition-transform duration-200 scale-0 rounded-full bg-primary-600/10 group-hover:scale-100"></div>
              </div>
              <span className="text-xl font-bold text-transparent text-gray-900 bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text">Maso Athu</span>
            </div>

            <div className="items-center hidden space-x-8 md:flex">
              <button
                onClick={() => scrollToSection('home')}
                className={`text-sm font-medium transition-all duration-200 relative ${activeSection === 'home' ? 'text-primary-600' : 'text-gray-700 hover:text-primary-600'
                  }`}
              >
                Home
                {activeSection === 'home' && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-600 rounded-full"></div>
                )}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className={`text-sm font-medium transition-all duration-200 relative ${activeSection === 'about' ? 'text-primary-600' : 'text-gray-700 hover:text-primary-600'
                  }`}
              >
                About Us
                {activeSection === 'about' && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-600 rounded-full"></div>
                )}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className={`text-sm font-medium transition-all duration-200 relative ${activeSection === 'contact' ? 'text-primary-600' : 'text-gray-700 hover:text-primary-600'
                  }`}
              >
                Contact
                {activeSection === 'contact' && (
                  <div className="absolute -bottom-1 left-0 right-0 h-0.5 bg-primary-600 rounded-full"></div>
                )}
              </button>
              <Link
                to="/login"
                className="bg-gradient-to-r from-primary-600 to-blue-600 text-white px-6 py-2.5 rounded-full hover:from-primary-700 hover:to-blue-700 transition-all duration-200 transform hover:scale-105 shadow-lg hover:shadow-xl"
              >
                <span className="flex items-center space-x-2">
                  <span>Admin Login</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 0v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                  </svg>
                </span>
              </Link>
            </div>

            {/* Mobile menu button */}
            <div className="md:hidden">
              <Link
                to="/login"
                className="bg-gradient-to-r from-primary-600 to-blue-600 text-white px-4 py-2.5 rounded-full text-sm hover:from-primary-700 hover:to-blue-700 transition-all duration-200 shadow-lg"
              >
                Login
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="relative flex items-center min-h-screen pt-16 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-primary-50">
        {/* Animated background shapes */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute rounded-full -top-40 -right-40 w-96 h-96 bg-gradient-to-br from-primary-200 to-blue-200 mix-blend-multiply filter blur-xl opacity-70 animate-bounce-slow"></div>
          <div className="absolute rounded-full -bottom-40 -left-40 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-200 mix-blend-multiply filter blur-xl opacity-70 animate-bounce-slow" style={{ animationDelay: '2s' }}></div>
          <div className="absolute rounded-full top-40 left-1/2 w-80 h-80 bg-gradient-to-br from-purple-200 to-pink-200 mix-blend-multiply filter blur-xl opacity-70 animate-bounce-slow" style={{ animationDelay: '4s' }}></div>
          {/* Additional floating elements */}
          <div className="absolute w-4 h-4 rounded-full top-20 right-20 bg-primary-400 animate-ping"></div>
          <div className="absolute w-3 h-3 bg-blue-400 rounded-full bottom-40 right-1/4 animate-ping" style={{ animationDelay: '1s' }}></div>
          <div className="absolute w-2 h-2 bg-purple-400 rounded-full top-1/3 left-20 animate-ping" style={{ animationDelay: '3s' }}></div>
        </div>
        <div className="relative z-10 px-4 py-20 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid items-center grid-cols-1 gap-12 lg:grid-cols-2">
            <div className={`transform transition-all duration-1000 ${isVisible ? 'translate-x-0 opacity-100' : '-translate-x-10 opacity-0'}`}>
              <div className="mb-6">
                <span className="inline-block px-4 py-2 mb-4 text-sm font-medium rounded-full bg-gradient-to-r from-primary-100 to-blue-100 text-primary-700">
                  🚀 Digital Democracy Platform
                </span>
              </div>
              <h1 className="mb-6 text-4xl font-bold leading-tight text-gray-900 md:text-6xl">
                <span className="text-transparent bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text lg:text-7xl">Maso Athu</span>
                <br />
                <span className="text-2xl font-medium text-gray-700 md:text-3xl">iReport Malawi</span>
              </h1>
              <p className="max-w-xl mb-8 text-xl leading-relaxed text-gray-600">
                A cutting-edge digital platform dedicated to monitoring, documenting, and reporting incidents of electoral violence, human rights violations, and threats to democratic integrity.
              </p>
              <div className="flex flex-col gap-4 mb-12 sm:flex-row">
                <button
                  onClick={() => scrollToSection('about')}
                  className="px-8 py-4 text-white transition-all duration-200 transform rounded-full shadow-lg bg-gradient-to-r from-primary-600 to-blue-600 hover:from-primary-700 hover:to-blue-700 hover:scale-105 hover:shadow-xl group"
                >
                  <span className="flex items-center justify-center space-x-2">
                    <span>Learn More</span>
                    <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                    </svg>
                  </span>
                </button>
                <Link
                  to="/login"
                  className="px-8 py-4 text-center transition-all duration-200 transform border-2 rounded-full shadow-lg border-primary-600 text-primary-600 hover:bg-primary-600 hover:text-white hover:scale-105 hover:shadow-xl group"
                >
                  <span className="flex items-center justify-center space-x-2">
                    <span>Access Portal</span>
                    <svg className="w-5 h-5 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 16l-4-4m0 0l4-4m-4 4h14m-5 0v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h7a3 3 0 013 3v1" />
                    </svg>
                  </span>
                </Link>
              </div>

              {/* Scroll indicator */}
              <div className="flex justify-center mt-16">
                <div className="animate-bounce">
                  <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                  </svg>
                </div>
              </div>
            </div>

            <div className={`transform transition-all duration-1000 delay-300 ${isVisible ? 'translate-x-0 opacity-100' : 'translate-x-10 opacity-0'}`}>
              <div className="relative">
                <div className="absolute inset-0 transform bg-gradient-to-r from-primary-400 to-blue-500 rounded-3xl rotate-3 opacity-20 animate-pulse-slow"></div>
                <div className="relative z-10 p-8 bg-white border shadow-2xl rounded-3xl border-gray-200/50 backdrop-blur-sm">
                  <div className="relative">
                    <img
                      src="/logo.png"
                      alt="Maso Athu Platform"
                      className="w-full h-auto max-w-sm mx-auto animate-float drop-shadow-2xl"
                    />
                    <div className="absolute inset-0 rounded-full bg-gradient-to-t from-transparent via-transparent to-white/10"></div>
                  </div>
                  <div className="mt-8 text-center">
                    <h3 className="mb-3 text-2xl font-bold text-transparent text-gray-900 bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text">Digital Democracy</h3>
                    <p className="leading-relaxed text-gray-600">Empowering transparent and peaceful elections through innovative technology</p>
                    <div className="flex justify-center mt-6 space-x-4">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span>Live Platform</span>
                      </div>
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
                        <span>Real-time Monitoring</span>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Floating badges */}
                <div className="absolute px-3 py-1 text-sm font-medium text-white rounded-full shadow-lg -top-4 -right-4 bg-gradient-to-r from-green-400 to-emerald-500 animate-bounce">
                  Secure
                </div>
                <div className="absolute px-3 py-1 text-sm font-medium text-white rounded-full shadow-lg -bottom-4 -left-4 bg-gradient-to-r from-blue-400 to-cyan-500 animate-bounce" style={{ animationDelay: '1s' }}>
                  24/7 Active
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-gradient-to-b from-white to-gray-50/50">
        <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <span className="inline-block px-4 py-2 mb-4 text-sm font-medium rounded-full bg-gradient-to-r from-primary-100 to-blue-100 text-primary-700">
              How It Works
            </span>
            <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl">Early Warning & Response System</h2>
            <p className="max-w-3xl mx-auto text-xl leading-relaxed text-gray-600">
              Through real-time data collection, verification, and analysis, iReport facilitates an Early Warning and Early Response (EWER) System.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {[
              {
                icon: (
                  <div className="relative">
                    <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <div className="absolute w-3 h-3 bg-green-400 rounded-full -top-1 -right-1 animate-pulse"></div>
                  </div>
                ),
                title: "Real-time Monitoring",
                description: "Continuous monitoring and documentation of electoral incidents and human rights violations with live data updates.",
                gradient: "from-blue-500 to-cyan-500"
              },
              {
                icon: (
                  <div className="relative">
                    <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div className="absolute w-3 h-3 bg-blue-400 rounded-full -top-1 -right-1 animate-pulse"></div>
                  </div>
                ),
                title: "Verification Process",
                description: "Thorough review and verification of reports in the Election Situation Room (ESR) ensuring data accuracy.",
                gradient: "from-green-500 to-emerald-500"
              },
              {
                icon: (
                  <div className="relative">
                    <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16c-.77.833.192 2.5 1.732 2.5z" />
                    </svg>
                    <div className="absolute w-3 h-3 bg-orange-400 rounded-full -top-1 -right-1 animate-pulse"></div>
                  </div>
                ),
                title: "Early Response",
                description: "Prompt response actions in collaboration with national stakeholders and partners for immediate intervention.",
                gradient: "from-red-500 to-pink-500"
              }
            ].map((feature, index) => (
              <div
                key={index}
                className={`group bg-white p-8 rounded-2xl shadow-lg border border-gray-100 transform transition-all duration-700 hover:scale-105 hover:shadow-2xl hover:-translate-y-2 relative overflow-hidden ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
                  }`}
                style={{ transitionDelay: `${index * 200}ms` }}
              >
                {/* Background gradient on hover */}
                <div className={`absolute inset-0 bg-gradient-to-r ${feature.gradient} opacity-0 group-hover:opacity-5 transition-opacity duration-300`}></div>

                <div className="relative z-10">
                  <div className="flex items-center justify-center w-16 h-16 mb-6 transition-transform duration-300 bg-gradient-to-r from-gray-50 to-gray-100 rounded-2xl group-hover:scale-110">
                    {feature.icon}
                  </div>
                  <h3 className="mb-4 text-xl font-bold text-gray-900 transition-colors duration-300 group-hover:text-primary-600">{feature.title}</h3>
                  <p className="leading-relaxed text-gray-600">{feature.description}</p>

                  {/* Learn more link */}
                  <div className="mt-6 transition-opacity duration-300 opacity-0 group-hover:opacity-100">
                    <span className="flex items-center space-x-1 text-sm font-medium text-primary-600">
                      <span>Learn more</span>
                      <svg className="w-4 h-4 transition-transform group-hover:translate-x-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                      </svg>
                    </span>
                  </div>
                </div>

                {/* Step number */}
                <div className="absolute flex items-center justify-center w-8 h-8 text-sm font-bold rounded-full top-4 right-4 bg-primary-100 text-primary-600">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>

          {/* Additional info section */}
          <div className="mt-16 text-center">
            <div className="max-w-4xl p-8 mx-auto bg-white border border-gray-100 shadow-lg rounded-2xl">
              <h3 className="mb-4 text-2xl font-bold text-gray-900">Trusted by Leading Organizations</h3>
              <p className="mb-6 text-gray-600">Our platform is utilized by government agencies, NGOs, and international organizations for electoral monitoring.</p>
              <div className="flex items-center justify-center space-x-8 opacity-60">
                <div className="w-2 h-2 rounded-full bg-primary-400 animate-pulse"></div>
                <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="relative py-20 overflow-hidden bg-gradient-to-b from-gray-50 to-white">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-32 h-32 rounded-full top-20 right-10 bg-primary-200/30 blur-3xl"></div>
          <div className="absolute w-40 h-40 rounded-full bottom-20 left-10 bg-blue-200/30 blur-3xl"></div>
        </div>

        <div className="relative z-10 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          {/* Main Implementers */}
          <div className="mb-16 text-center">
            <span className="inline-block px-4 py-2 mb-4 text-sm font-medium rounded-full bg-gradient-to-r from-primary-100 to-blue-100 text-primary-700">
              Our Partners
            </span>
            <h2 className="mb-8 text-3xl font-bold text-gray-900 md:text-4xl">Implemented by</h2>
            <div className="flex items-center justify-center mb-12 space-x-8">
              <div className="p-8 transition-all duration-300 transform bg-white border border-gray-200 shadow-lg rounded-2xl hover:shadow-xl hover:scale-105">
                <img src="/nice_logo.png" alt="NICE" className="w-auto h-16 mx-auto" />
              </div>
              <div className="p-8 transition-all duration-300 transform bg-white border border-gray-200 shadow-lg rounded-2xl hover:shadow-xl hover:scale-105">
                <img src="/MPAUC_logo.png" alt="MPUC" className="w-auto h-16 mx-auto" />
              </div>
            </div>
          </div>

          <div className="grid items-center grid-cols-1 gap-12 mb-16 lg:grid-cols-2">
            <div className="space-y-6">
              <div className="inline-block px-4 py-2 mb-4 text-sm font-medium rounded-full bg-gradient-to-r from-primary-100 to-blue-100 text-primary-700">
                About Our Platform
              </div>
              <h3 className="text-3xl font-bold leading-tight text-gray-900 md:text-4xl">
                About <span className="text-transparent bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text">Maso Athu</span> (iReport)
                About <span className="text-transparent bg-gradient-to-r from-primary-600 to-blue-600 bg-clip-text lg:text-5xl">Maso Athu</span> (iReport)
              </h3>
              <p className="text-lg leading-relaxed text-gray-700">
                Maso Athu (iReport) Malawi is a cutting-edge digital platform dedicated to monitoring, documenting, and reporting incidents of electoral violence, human rights violations, and other threats to democratic integrity.
              </p>
              <p className="text-lg leading-relaxed text-gray-700">
                Hosted by the <strong className="text-primary-600">National Initiative for Civic Education (NICE)</strong> and the <strong className="text-primary-600">Malawi Peace and Unity Commission (MPUC)</strong>, iReport is supported by the <strong className="text-primary-600">United Nations Development Programme (UNDP)</strong>, which provides technical expertise and financial support.
              </p>
              <p className="text-lg leading-relaxed text-gray-700">
                The objective of Maso Athu is to detect, report and inform about the institutional response in the early phase to possible incidents linked to electoral processes throughout the national territory, facilitating a multi-actor response when necessary.
              </p>

              {/* Key features */}
              <div className="grid grid-cols-1 gap-4 pt-6 sm:grid-cols-2">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-r from-green-400 to-emerald-500">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-700">Real-time incident reporting</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-r from-blue-400 to-cyan-500">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-700">Human rights protection</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-r from-purple-400 to-pink-500">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-700">Democratic integrity</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex items-center justify-center w-5 h-5 rounded-full bg-gradient-to-r from-red-400 to-orange-500">
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <span className="font-medium text-gray-700">Multi-stakeholder collaboration</span>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 transform bg-gradient-to-r from-green-400 to-blue-500 rounded-3xl -rotate-3 opacity-20"></div>
              <div className="relative z-10 group">
                <img
                  src="/about_us.png"
                  alt="About Maso Athu"
                  className="w-full h-auto transition-transform duration-300 shadow-2xl rounded-3xl group-hover:scale-105"
                />
                {/* Overlay with stats */}
                <div className="absolute inset-0 flex items-end transition-opacity duration-300 opacity-0 bg-gradient-to-t from-black/50 via-transparent to-transparent rounded-3xl group-hover:opacity-100">
                  <div className="p-6 text-white">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <div className="text-2xl font-bold">24/7</div>
                        <div className="text-sm opacity-90">Monitoring</div>
                      </div>
                      <div>
                        <div className="text-2xl font-bold">Real-time</div>
                        <div className="text-sm opacity-90">Alerts</div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Mission Section */}
          <div className="p-8 mb-16 border shadow-lg bg-gradient-to-r from-white to-primary-50/50 rounded-3xl border-gray-200/50">
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-6 bg-gradient-to-r from-primary-500 to-blue-500 rounded-2xl">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              </div>
              <h3 className="mb-6 text-3xl font-bold text-gray-900">Our Mission</h3>
              <p className="max-w-4xl mx-auto mb-8 text-xl leading-relaxed text-gray-700">
                To safeguard free, fair, and peaceful elections through innovative technology and collaborative partnerships, ensuring democratic integrity across Malawi.
              </p>
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="flex items-center justify-center p-4 space-x-3 bg-white border border-gray-100 shadow-sm rounded-2xl">
                  <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gradient-to-r from-primary-500 to-primary-600"></div>
                  <span className="font-medium text-gray-700">Real-time incident reporting</span>
                </div>
                <div className="flex items-center justify-center p-4 space-x-3 bg-white border border-gray-100 shadow-sm rounded-2xl">
                  <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-blue-600"></div>
                  <span className="font-medium text-gray-700">Human rights protection</span>
                </div>
                <div className="flex items-center justify-center p-4 space-x-3 bg-white border border-gray-100 shadow-sm rounded-2xl">
                  <div className="flex-shrink-0 w-4 h-4 rounded-full bg-gradient-to-r from-green-500 to-green-600"></div>
                  <span className="font-medium text-gray-700">Democratic integrity</span>
                </div>
              </div>
            </div>
          </div>

          {/* Partners Section */}
          <div className="text-center">
            <h3 className="mb-8 text-3xl font-bold text-gray-900">Supported by our Partners</h3>
            <div className="grid grid-cols-2 gap-6 mb-8 md:grid-cols-3 lg:grid-cols-6">
              {[
                { src: "/UNDP-Logo-Blue-Large-Transparent.png", alt: "UNDP" },
                { src: "/Embassy_of_Ireland.jpg", alt: "Embassy of Ireland" },
                { src: "/USAID-Identity.svg.png", alt: "USAID" },
                { src: "/UK_International_Development_logo.png", alt: "FCDO" },
                { src: "/Norway_Logo_Positive_RGB.png", alt: "Royal Norwegian Embassy" },
                { src: "/UN_Women_logo_en.png", alt: "UN Women" }
              ].map((partner, index) => (
                <div key={index} className="p-6 transition-all duration-300 transform bg-white border border-gray-100 shadow-md rounded-2xl hover:shadow-xl hover:scale-105 group">
                  <img
                    src={partner.src}
                    alt={partner.alt}
                    className="w-auto h-12 mx-auto transition-transform duration-300 group-hover:scale-110"
                  />
                </div>
              ))}
            </div>

            {/* European Union text */}
            <div className="inline-block p-6 bg-white border border-gray-100 shadow-md rounded-2xl">
              <p className="text-sm font-medium text-gray-600">
                Also supported by the <strong className="text-primary-600">European Union (EU)</strong>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="relative py-20 overflow-hidden bg-gradient-to-b from-white to-gray-50">
        {/* Background decorations */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-20 h-20 rounded-full top-10 left-20 bg-primary-200/30 blur-2xl"></div>
          <div className="absolute w-32 h-32 rounded-full bottom-20 right-20 bg-blue-200/30 blur-3xl"></div>
        </div>

        <div className="relative z-10 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="mb-16 text-center">
            <span className="inline-block px-4 py-2 mb-4 text-sm font-medium rounded-full bg-gradient-to-r from-primary-100 to-blue-100 text-primary-700">
              Contact Us
            </span>
            <h2 className="mb-6 text-3xl font-bold text-gray-900 md:text-4xl">Get In Touch</h2>
            <p className="max-w-3xl mx-auto text-xl leading-relaxed text-gray-600">
              Have questions about Maso Athu or need support? We're here to help.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-12 md:grid-cols-2">
            <div className="space-y-8">
              {[
                {
                  icon: (
                    <svg className="w-6 h-6 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                  ),
                  title: "Address",
                  content: "Lilongwe, Malawi",
                  gradient: "from-blue-500 to-cyan-500"
                },
                {
                  icon: (
                    <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                  ),
                  title: "Email",
                  content: "info@ews.mw",
                  gradient: "from-green-500 to-emerald-500"
                },
                {
                  icon: (
                    <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                  ),
                  title: "Phone",
                  content: "+265 xxx xxx xxx",
                  gradient: "from-purple-500 to-pink-500"
                }
              ].map((item, index) => (
                <div key={index} className="flex items-start space-x-4 group">
                  <div className={`w-14 h-14 bg-gradient-to-r ${item.gradient} rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300 shadow-lg`}>
                    {item.icon}
                  </div>
                  <div>
                    <h3 className="mb-1 text-lg font-bold text-gray-900">{item.title}</h3>
                    <p className="text-gray-600">{item.content}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="p-8 border shadow-lg bg-gradient-to-br from-gray-50 to-white rounded-3xl border-gray-200/50">
              <form className="space-y-6">
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">Name</label>
                  <input
                    type="text"
                    className="w-full px-4 py-3 transition-all duration-200 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="Your full name"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">Email</label>
                  <input
                    type="email"
                    className="w-full px-4 py-3 transition-all duration-200 border border-gray-300 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="your.email@example.com"
                  />
                </div>
                <div>
                  <label className="block mb-2 text-sm font-medium text-gray-700">Message</label>
                  <textarea
                    rows={4}
                    className="w-full px-4 py-3 transition-all duration-200 border border-gray-300 resize-none rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                    placeholder="How can we help you?"
                  ></textarea>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 font-medium text-white transition-all duration-200 transform shadow-lg bg-gradient-to-r from-primary-600 to-blue-600 rounded-2xl hover:from-primary-700 hover:to-blue-700 hover:scale-105 hover:shadow-xl"
                >
                  Send Message
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="relative py-12 overflow-hidden text-white bg-gradient-to-b from-gray-900 to-black">
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-primary-600/20 to-blue-600/20"></div>
        </div>

        <div className="relative z-10 px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div>
              <div className="flex items-center mb-6 space-x-3">
                <div className="relative">
                  <img src="/logo.png" alt="Maso Athu" className="w-10 h-10" />
                  <div className="absolute inset-0 scale-150 rounded-full bg-primary-600/20 animate-pulse"></div>
                </div>
                <span className="text-2xl font-bold text-transparent bg-gradient-to-r from-white to-gray-300 bg-clip-text">Maso Athu</span>
              </div>
              <p className="mb-6 leading-relaxed text-gray-400">
                Safeguarding democratic integrity through innovative technology and collaborative partnerships.
              </p>
              <div className="flex space-x-4">
                <div className="flex items-center justify-center w-10 h-10 transition-transform duration-200 rounded-full cursor-pointer bg-gradient-to-r from-primary-600 to-blue-600 hover:scale-110">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M24 4.557c-.883.392-1.832.656-2.828.775 1.017-.609 1.798-1.574 2.165-2.724-.951.564-2.005.974-3.127 1.195-.897-.957-2.178-1.555-3.594-1.555-3.179 0-5.515 2.966-4.797 6.045-4.091-.205-7.719-2.165-10.148-5.144-1.29 2.213-.669 5.108 1.523 6.574-.806-.026-1.566-.247-2.229-.616-.054 2.281 1.581 4.415 3.949 4.89-.693.188-1.452.232-2.224.084.626 1.956 2.444 3.379 4.6 3.419-2.07 1.623-4.678 2.348-7.29 2.04 2.179 1.397 4.768 2.212 7.548 2.212 9.142 0 14.307-7.721 13.995-14.646.962-.695 1.797-1.562 2.457-2.549z" />
                  </svg>
                </div>
                <div className="flex items-center justify-center w-10 h-10 transition-transform duration-200 rounded-full cursor-pointer bg-gradient-to-r from-blue-600 to-cyan-600 hover:scale-110">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.566-1.35 2.14-2.21z" />
                  </svg>
                </div>
                <div className="flex items-center justify-center w-10 h-10 transition-transform duration-200 rounded-full cursor-pointer bg-gradient-to-r from-green-600 to-emerald-600 hover:scale-110">
                  <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                  </svg>
                </div>
              </div>
            </div>

            <div>
              <h3 className="mb-6 text-lg font-bold text-white">Quick Links</h3>
              <ul className="space-y-3 text-gray-400">
                <li>
                  <button
                    onClick={() => scrollToSection('home')}
                    className="flex items-center space-x-2 transition-colors duration-200 hover:text-white group"
                  >
                    <span className="w-1.5 h-1.5 bg-primary-500 rounded-full group-hover:scale-150 transition-transform duration-200"></span>
                    <span>Home</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('about')}
                    className="flex items-center space-x-2 transition-colors duration-200 hover:text-white group"
                  >
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full group-hover:scale-150 transition-transform duration-200"></span>
                    <span>About Us</span>
                  </button>
                </li>
                <li>
                  <button
                    onClick={() => scrollToSection('contact')}
                    className="flex items-center space-x-2 transition-colors duration-200 hover:text-white group"
                  >
                    <span className="w-1.5 h-1.5 bg-green-500 rounded-full group-hover:scale-150 transition-transform duration-200"></span>
                    <span>Contact</span>
                  </button>
                </li>
                <li>
                  <Link
                    to="/login"
                    className="flex items-center space-x-2 transition-colors duration-200 hover:text-white group"
                  >
                    <span className="w-1.5 h-1.5 bg-purple-500 rounded-full group-hover:scale-150 transition-transform duration-200"></span>
                    <span>Admin Login</span>
                  </Link>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="mb-6 text-lg font-bold text-white">Key Partners</h3>
              <ul className="space-y-2 text-sm text-gray-400">
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-primary-500 to-blue-500"></div>
                  <span>NICE</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"></div>
                  <span>MPUC</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-cyan-500 to-green-500"></div>
                  <span>UNDP</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-green-500 to-emerald-500"></div>
                  <span>European Union</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-emerald-500 to-purple-500"></div>
                  <span>Embassy of Ireland</span>
                </li>
                <li className="flex items-center space-x-2">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-purple-500 to-pink-500"></div>
                  <span>USAID & FCDO</span>
                </li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col items-center justify-between pt-8 mt-8 border-t border-gray-800 md:flex-row">
            <p className="mb-4 text-gray-400 md:mb-0">&copy; 2025 Maso Athu (iReport) Malawi. All rights reserved.</p>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-500">Powered by</span>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 rounded-full bg-primary-500 animate-pulse"></div>
                <span className="text-sm font-medium text-gray-400">Digital Democracy</span>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
